import React from "react"
import { useTranslation } from "react-i18next"

import { Result } from "@treefort/lib/result"

import { AsyncViewOfflineProvider } from "../../components/async-view-offline-provider"
import useAppManifest from "../../hooks/use-app-manifest"
import { useAsyncViewPropsForQueries } from "../../hooks/use-async-view-props-for-queries"
import useContent from "../../hooks/use-content"
import { useRoute } from "../../hooks/use-route"
import { ModuleLayout } from "../layouts/module"
import { AudiobookListViewModule } from "../layouts/module/list-view-modules/audiobook"
import { CardListViewModule } from "../layouts/module/list-view-modules/card"
import { SliderListViewModule } from "../layouts/module/list-view-modules/slider"

export function ContentAudiobookScreen(): React.JSX.Element {
  const manifest = useAppManifest()
  const { params } = useRoute<"contentId">()
  const { t } = useTranslation()

  if (!params.contentId) {
    throw new Error(`Visited the audiobook screen without a content id!`)
  }

  const contentId = parseInt(params.contentId)

  const audiobook = useContent(contentId, "book")
  const relatedEbook = useContent(audiobook.data?.details.relatedEbookId)

  const listViewModules = Result.mapSuccess(audiobook, (audiobook) =>
    Result.success([
      new AudiobookListViewModule({
        id: 0,
        type: "book",
        config: { contentId },
        conditions: {},
      }),
      ...(audiobook.details.relatedEbookId || audiobook.links.length
        ? [
            new CardListViewModule({
              id: -1,
              type: "card",
              config: {
                items: [
                  ...(audiobook.details.relatedEbookId
                    ? [
                        {
                          type: "content",
                          appLink: {
                            type: "content",
                            contentType: "ebook",
                            contentId: audiobook.details.relatedEbookId,
                          },
                          title: t("Read the eBook"),
                          subtitle: relatedEbook.data?.title || null,
                        } as const,
                      ]
                    : []),
                  ...audiobook.links,
                ],
              },
              conditions: {},
            }),
          ]
        : []),
      ...(manifest.features.contentPageRecommendations
        ? [
            new SliderListViewModule(
              {
                id: -2,
                type: "slider",
                config: {
                  title: t("Recommendations"),
                  itemShape: "wide",
                  size: "medium",
                  showItemTitles: true,
                  items: {
                    type: "recombee",
                    recommendation: {
                      type: "itemsToItem",
                      contentId,
                      personalized:
                        manifest.features.contentPageRecommendations
                          .personalized,
                      scenario:
                        manifest.features.contentPageRecommendations.scenario,
                    },
                  },
                },
                conditions: {},
              },
              { key: `content-page-recommendations-${contentId}` },
            ),
          ]
        : []),
    ]),
  )

  const asyncViewProps = useAsyncViewPropsForQueries(audiobook)
  const key = `audiobook-page-${contentId}`

  return (
    <AsyncViewOfflineProvider
      key={key}
      {
        // If we failed to load what we need at this level then provide that
        // information to the async view so that an appropriate error message
        // can be rendered. Otherwise delegate the async view props to our
        // children (by omitting them here).
        ...(asyncViewProps.state === "error" ? asyncViewProps : null)
      }
    >
      <ModuleLayout pageKey={key} listViewModules={listViewModules} />
    </AsyncViewOfflineProvider>
  )
}
